import React, { useMemo } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { useClientSelfTransactions, useClientSelf } from '@/hooks/use-api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Printer, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
export function ClientPassbook() {
  const { data: client, isLoading: clientLoading } = useClientSelf();
  const { data: txnsData, isLoading: txnsLoading } = useClientSelfTransactions();
  const handlePrint = () => {
    window.print();
  };
  const transactionsWithBalance = useMemo(() => {
    if (!txnsData?.items) return [];
    // Calculate running balance in ascending order
    const chronTxns = [...txnsData.items].sort((a, b) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
    let currentSum = 0;
    const history = chronTxns.map(txn => {
      if (txn.type === 'Collection') {
        currentSum += txn.amount;
      } else {
        currentSum -= txn.amount;
      }
      return { ...txn, runningBalance: currentSum };
    });
    // Return descending for display
    return history.reverse();
  }, [txnsData]);
  const isLoading = clientLoading || txnsLoading;
  return (
    <AppLayout container className="bg-background scrollbar-custom">
      <div className="space-y-8 print:p-0 animate-fade-in">
        <div className="flex items-center justify-between no-print">
          <div>
            <h1 className="text-3xl font-black text-foreground tracking-tight uppercase">Member Passbook</h1>
            <p className="text-muted-foreground text-[10px] font-bold uppercase tracking-widest mt-1">Official verified record of all financial movements.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handlePrint} disabled={!client} className="rounded-xl border-border text-muted-foreground font-bold">
              <Printer className="h-4 w-4 mr-2" />
              Print Official Copy
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-border bg-card shadow-soft rounded-2xl print:border">
            <CardHeader className="pb-2">
              <CardTitle className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Account Name</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? <Skeleton className="h-6 w-32" /> : <p className="text-xl font-black text-foreground">{client?.name || "Verified Member"}</p>}
            </CardContent>
          </Card>
          <Card className="border-border bg-card shadow-soft rounded-2xl print:border">
            <CardHeader className="pb-2">
              <CardTitle className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Liquid Balance</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? <Skeleton className="h-8 w-24" /> : <p className="text-2xl font-black text-emerald-500">GHS {client?.balance?.toFixed(2) || "0.00"}</p>}
            </CardContent>
          </Card>
          <Card className="border-border bg-card shadow-soft rounded-2xl print:border">
            <CardHeader className="pb-2">
              <CardTitle className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Verification Status</CardTitle>
            </CardHeader>
            <CardContent>
              <Badge className="bg-primary text-primary-foreground border-none font-bold text-[10px] px-3">ACTIVE & AUDITED</Badge>
            </CardContent>
          </Card>
        </div>
        <div className="rounded-3xl border-border overflow-hidden shadow-soft bg-card scrollbar-custom">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-muted/50">
                <TableRow className="border-border">
                  <TableHead className="w-[200px] text-[10px] font-black uppercase tracking-widest text-muted-foreground py-4 pl-6">Timestamp</TableHead>
                  <TableHead className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Transaction Type</TableHead>
                  <TableHead className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Audit Description</TableHead>
                  <TableHead className="text-right text-[10px] font-black uppercase tracking-widest text-muted-foreground">Value (GHS)</TableHead>
                  <TableHead className="text-right text-[10px] font-black uppercase tracking-widest text-muted-foreground pr-6">Running Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={5} className="py-8"><Skeleton className="h-8 w-full" /></TableCell>
                    </TableRow>
                  ))
                ) : transactionsWithBalance.length > 0 ? (
                  transactionsWithBalance.map((txn) => (
                    <TableRow key={txn.id} className="hover:bg-muted/50 transition-colors border-border">
                      <TableCell className="font-mono text-[10px] text-muted-foreground pl-6 py-4">
                        {new Date(txn.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {txn.type === 'Collection' ? (
                            <ArrowUpCircle className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <ArrowDownCircle className="h-4 w-4 text-destructive" />
                          )}
                          <span className="font-black text-foreground text-xs uppercase tracking-tight">{txn.type}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground font-bold uppercase tracking-tight">
                        {txn.type === 'Collection' ? "Daily Susu Contribution" : "Institutional Record Entry"}
                      </TableCell>
                      <TableCell className={cn(
                        "text-right font-black",
                        txn.type === 'Collection' ? "text-emerald-500" : "text-destructive"
                      )}>
                        {txn.type === 'Collection' ? '+' : '-'} {txn.amount.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-mono font-black text-foreground pr-6">
                        GHS {txn.runningBalance.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="h-48 text-center text-muted-foreground font-black uppercase tracking-[0.2em] text-[10px]">
                      No transactions recorded in digital vault.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}